// dashboard/frontend/src/components/Gmail/GmailAccountList.js
import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    IconButton,
    Alert,
    Chip,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    TrendingDown as TrendingDownIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../api/axios';

// Renders "42.9% (9/21)" for a deliverability window, or an em dash when
// there were no sends in the window.
const formatOpenRate = (window) => {
    if (!window || window.sends === 0 || window.human_open_rate === null) {
        return '—';
    }
    return `${window.human_open_rate}% (${window.human_opened}/${window.sends})`;
};

// Trend chip per account. 'degrading' is the real alarm: the 7d human open
// rate fell to half (or less) of the prior 7d, with enough sends in both
// windows to trust it. 'low_sample' means not enough sends to judge.
const TrendChip = ({ status }) => {
    if (status === 'degrading') {
        return (
            <Tooltip title="Human open rate fell to ≤50% of this account's prior 7-day window — possible deliverability/spam problem">
                <Chip
                    label="Degrading"
                    color="error"
                    size="small"
                    icon={<TrendingDownIcon />}
                />
            </Tooltip>
        );
    }
    if (status === 'healthy') {
        return (
            <Tooltip title="7-day human open rate is holding up against this account's prior 7-day window">
                <Chip
                    label="Healthy"
                    color="success"
                    size="small"
                    icon={<CheckCircleOutlineIcon />}
                />
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Not enough sends in the current or prior 7-day window to judge a trend (minimum 10 each)">
            <Chip label="Low sample" color="default" size="small" />
        </Tooltip>
    );
};

const GmailAccountList = () => {
    const [accounts, setAccounts] = useState([]);
    // Map of account id -> deliverability payload from the backend.
    const [deliverability, setDeliverability] = useState({});
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            console.log('Fetching accounts and deliverability...');
            const [accountsRes, deliverabilityRes] = await Promise.all([
                api.get('/gmail-accounts/'),
                api.get('/gmail-accounts/deliverability/'),
            ]);
            console.log('Accounts response:', accountsRes.data);
            console.log('Deliverability response:', deliverabilityRes.data);

            // Handle paginated response
            setAccounts(accountsRes.data.results || []);

            // Index deliverability rows by account id for O(1) lookup per row
            const byId = {};
            (deliverabilityRes.data.accounts || []).forEach((row) => {
                byId[row.id] = row;
            });
            setDeliverability(byId);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setError('Failed to load Gmail accounts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    if (loading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography>Loading Gmail accounts...</Typography>
            </Paper>
        );
    }

    const handleToggleActive = async (accountId, currentStatus) => {
        try {
            await api.patch(`/gmail-accounts/${accountId}/`, {
                is_active: !currentStatus
            });
            fetchAccounts();
            setSuccess('Account status updated successfully');
        } catch (error) {
            setError('Failed to update account status');
        }
    };

    const handleRefreshAuth = (email) => {
        // Re-authorize via the OAuth start flow. Tokens in a Google "Testing"
        // app expire every ~7 days, so a full re-auth is the reliable fix.
        // login_hint pre-selects the correct Google account so we don't
        // accidentally re-authorize the wrong one.
        window.location.href = `/api/oauth/start/?login_hint=${encodeURIComponent(email)}`;
    };

    const handleAddAccount = async () => {
        try {
            // Redirect directly to the OAuth start endpoint
            window.location.href = '/api/oauth/start/';
        } catch (error) {
            setError('Failed to initiate account addition');
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    Gmail Accounts ({accounts.length})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                >
                    Add Account
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {accounts.length === 0 ? (
                <Typography color="textSecondary" sx={{ my: 2 }}>
                    No Gmail accounts configured. Click "Add Account" to get started.
                </Typography>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Daily Usage</TableCell>
                                <TableCell>Open Rate (7d)</TableCell>
                                <TableCell>Open Rate (30d)</TableCell>
                                <TableCell>Trend</TableCell>
                                <TableCell>Last Used</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accounts.map((account) => {
                                const stats = deliverability[account.id];
                                return (
                                    <TableRow key={account.id}>
                                        <TableCell>{account.email}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={account.is_active}
                                                onChange={() => handleToggleActive(account.id, account.is_active)}
                                            />
                                            <Chip
                                                label={account.is_active ? 'Active' : 'Inactive'}
                                                color={account.is_active ? 'success' : 'default'}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                Sent Today: {stats ? stats.sent_today : '—'}
                                                <br />
                                                Remaining: {stats ? stats.remaining_today : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatOpenRate(stats && stats.window_7d)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatOpenRate(stats && stats.window_30d)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {stats ? <TrendChip status={stats.trend_status} /> : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {account.last_used
                                                ? format(new Date(account.last_used), 'MMM d, yyyy HH:mm')
                                                : 'Never'}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleRefreshAuth(account.email)}
                                                title="Re-authorize (sign in again with Google)"
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
                Open rates count distinct emails with at least one human open. They
                undercount reality (proxy-only Gmail reads are invisible to our
                tracking), so watch the trend per account, not the absolute number.
                "Sent Today" is computed from detected sends and can lag by ~15
                minutes behind the poller.
            </Typography>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Gmail Account</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Gmail Address"
                        type="email"
                        fullWidth
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddAccount} variant="contained">
                        Add Account
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default GmailAccountList;